/*
 * LÓGICA DE LA CORRECCIÓN DEL SALDO INICIAL (H-17, incremento 11/16).
 *
 * Está fuera del formulario a propósito (RNF-016): la vista previa, los tres
 * estados de advertencia y los textos de la confirmación se pueden probar sin
 * montar la pantalla, y la pantalla queda sin una sola decisión dentro.
 *
 * AQUÍ NO SE LLAMA AL SERVIDOR Y NO SE INVENTA NINGUNA CIFRA. Las tres cantidades
 * de la vista previa salen de lo que `CuentaDto` YA trae —`saldo`, `deuda`,
 * `saldoInicial`— con la única aritmética que la regla autoriza (RF-173):
 *
 *     resultante = saldo actual − saldo inicial anterior + saldo inicial nuevo
 *
 * Es aritmética de presentación, no un cálculo de negocio: el valor que manda es
 * el que devuelve el servidor al guardar, y es ese el que se presenta al volver
 * (RN-543).
 */

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';

/** Los tres estados EXCLUYENTES de la advertencia (RN-537c, RN-538). */
export const ESTADOS_DEL_RESULTADO = Object.freeze({
  CERO: 'CERO',
  POSITIVO: 'POSITIVO',
  NEGATIVO: 'NEGATIVO',
});

/** Centavos enteros desde una cadena decimal. Devuelve NaN si no es un número. */
export function aCentavos(cadena) {
  if (cadena === null || cadena === undefined) return NaN;
  const texto = String(cadena).trim().replace(/,/g, '');
  if (texto === '' || !/^-?\d+(\.\d+)?$/.test(texto)) return NaN;
  return Math.round(Number(texto) * 100);
}

function deCentavos(centavos) {
  return (centavos / 100).toFixed(2);
}

/**
 * Qué estado tiene el saldo resultante. Cero exacto es su propio caso y NO se
 * mezcla con el positivo: con cero la cuenta ya se puede desactivar y no hay nada
 * que advertir (RN-538).
 */
export function estadoDelResultado(centavosResultantes) {
  if (centavosResultantes === 0) return ESTADOS_DEL_RESULTADO.CERO;
  return centavosResultantes > 0 ? ESTADOS_DEL_RESULTADO.POSITIVO : ESTADOS_DEL_RESULTADO.NEGATIVO;
}

/**
 * Vista previa del efecto de la corrección (RF-173).
 *
 * @param cuenta            el `CuentaDto` tal como llegó.
 * @param saldoInicialNuevo lo que hay escrito en el campo, en crudo.
 * @returns {{hayCambio:boolean, valido:boolean, anterior:string, nuevo:string,
 *            resultante:string, estado:string, centavosResultantes:number}}
 */
export function calcularVistaPrevia(cuenta, saldoInicialNuevo) {
  const anterior = aCentavos(cuenta?.saldoInicial);
  const nuevo = aCentavos(saldoInicialNuevo);

  // El saldo actual: en una tarjeta es la DEUDA. Si el servidor no pudo
  // calcularlo, la vista previa no se pinta: no se inventa una cifra base
  // (RN-547, criterio de RN-069f).
  const actualCrudo = cuenta?.esCredito ? cuenta?.deuda : cuenta?.saldo;
  const actual = aCentavos(actualCrudo);

  const valido = Number.isFinite(nuevo) && Number.isFinite(anterior) && Number.isFinite(actual) && nuevo >= 0;
  const hayCambio = valido && nuevo !== anterior;
  const centavosResultantes = valido ? actual - anterior + nuevo : NaN;

  return {
    valido,
    hayCambio,
    centavosResultantes,
    anterior: Number.isFinite(anterior) ? deCentavos(anterior) : null,
    nuevo: Number.isFinite(nuevo) ? deCentavos(nuevo) : null,
    resultante: Number.isFinite(centavosResultantes) ? deCentavos(centavosResultantes) : null,
    estado: Number.isFinite(centavosResultantes) ? estadoDelResultado(centavosResultantes) : null,
  };
}

/**
 * La advertencia que toca, en los TRES casos excluyentes de RN-538, con los
 * literales de RN-537a y RN-537b palabra por palabra.
 *
 * Devuelve `null` cuando el resultado es CERO: ahí no se pinta advertencia
 * ninguna. Y como se recalcula en cada pulsación, la advertencia DESAPARECE sola
 * en cuanto el resultado deja de ser negativo (RN-537c): un aviso que se queda
 * encendido cuando ya no es verdad enseña a ignorar los avisos.
 */
export function advertenciaDelResultado(cuenta, vistaPrevia) {
  if (!vistaPrevia?.valido) return null;

  const divisa = cuenta?.codigoISO;
  const cero = formatearImporte('0.00', divisa);

  if (vistaPrevia.estado === ESTADOS_DEL_RESULTADO.CERO) return null;

  if (vistaPrevia.estado === ESTADOS_DEL_RESULTADO.POSITIVO) {
    return {
      tono: 'w',
      texto: `Mientras el saldo no sea exactamente ${cero}, esta ${cuenta?.esCredito ? 'tarjeta' : 'cuenta'} no se puede desactivar.`,
    };
  }

  // NEGATIVO. En una tarjeta el signo menos significa dinero A TU FAVOR, no deuda,
  // y el texto no puede decir «en contra» (RN-537b).
  const conSigno = formatearImporte(vistaPrevia.resultante, divisa);
  const sinSigno = formatearImporte(String(Math.abs(Number(vistaPrevia.resultante))), divisa);

  return cuenta?.esCredito
    ? {
      tono: 'e',
      texto: `Ojo: con este cambio esta tarjeta queda con ${sinSigno} a tu favor. Puedes guardarlo así, pero no podrás desactivarla hasta que su deuda sea exactamente ${cero}.`,
    }
    : {
      tono: 'e',
      texto: `Ojo: con este cambio el saldo de esta cuenta queda en ${conSigno}, es decir, en contra. Puedes guardarlo así, pero no podrás desactivar la cuenta hasta que su saldo sea exactamente ${cero}.`,
    };
}

/**
 * Los textos de la confirmación (RN-538), ya resueltos en castellano para el
 * componente genérico del núcleo: él los PINTA, no los calcula (§7 bis, RN-495).
 */
export function textosDeLaConfirmacion(cuenta, vistaPrevia, motivo) {
  const divisa = cuenta?.codigoISO;
  const esCredito = Boolean(cuenta?.esCredito);
  const frase = esCredito ? 'la deuda inicial' : 'el saldo inicial';
  const fraseConMayuscula = esCredito ? 'La deuda inicial' : 'El saldo inicial';
  const objeto = esCredito ? 'la deuda de la tarjeta' : 'el saldo de la cuenta';
  const etiqueta = esCredito ? 'Deuda inicial' : 'Saldo inicial';

  const anterior = formatearImporte(vistaPrevia.anterior, divisa);
  const nuevo = formatearImporte(vistaPrevia.nuevo, divisa);
  const resultante = formatearImporte(vistaPrevia.resultante, divisa);
  const motivoLimpio = (motivo ?? '').trim();

  const detalle = [
    { clave: 'anterior', texto: `${etiqueta} de ahora`, dato: anterior },
    { clave: 'nuevo', texto: `${etiqueta} corregido`, dato: nuevo },
    { clave: 'resultante', texto: esCredito ? 'Deuda de la tarjeta' : 'Saldo de la cuenta', dato: resultante },
  ];

  // El motivo escrito entra en el detalle; si no se anotó, no se inventa renglón.
  if (motivoLimpio) detalle.push({ clave: 'motivo', texto: 'Motivo que anotaste', dato: motivoLimpio });

  return {
    pregunta: `¿Corregir ${frase} de “${cuenta?.nombre ?? ''}”?`,
    consecuencia: `${fraseConMayuscula} pasa de ${anterior} a ${nuevo}. Con eso, ${objeto} queda en ${resultante}. `
      + 'Tus movimientos no cambian: no se reescribe, no se cancela y no se crea ninguno.',
    detalle,
    advertencia: advertenciaDelResultado(cuenta, vistaPrevia)?.texto ?? null,
    rotuloDeAceptar: `Sí, corregir ${frase}`,
    rotuloEnCurso: 'Corrigiendo…',
  };
}
